import { yupResolver } from '@hookform/resolvers/yup'
import { Alert, Box, Stack, Typography } from '@mui/material'
import { C1Button } from 'c1ui'
import { SuccessCheck } from 'components/core/action-icons/success-check'
import { CIDRChipInput } from 'components/core/chipinput'
import { C1CopyText } from 'components/core/copytext/C1CopyText'
import { C1DrawerInner } from 'components/core/drawer/C1DrawerInner'
import { APIError } from 'components/core/errors/api'
import { C1FormLabel } from 'components/core/RHKFormComponents/C1FormLabel'
import { C1LabeledRadioGroup } from 'components/core/RHKFormComponents/C1LabeledRadioGroup'
import { C1LabeledSelect } from 'components/core/RHKFormComponents/C1LabeledSelect'
import { C1LabeledSwitch } from 'components/core/RHKFormComponents/C1LabeledSwitch'
import { TextField } from 'components/core/textfield'
import { ClosableProps } from 'components/no-ui/context/modal-provider'
import { RoleID } from 'lib/constants/system'
import { Role, SearchRolesRequest } from 'pbts/c1/api/iam/v1/iam'
import { ServicePrincipalServiceCreateCredentialRequest } from 'pbts/c1/api/service_principal/v1/service_principal'
import { Duration } from 'pbts/google/protobuf/duration'
import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { useGetAllRoles } from 'redux_toolkit/roles'
import { createServicePrincipalCredentialApi } from 'redux_toolkit/servicePrincipals'
import * as yup from 'yup'

type CreateSPCredentialDrawerProps = ClosableProps & {
    servicePrincipalId: string
}

type FormData = {
    displayName: string
    expiresDays: number
    allowSourceCidrs: string[]
    scopedRole: string
    requireDpop: boolean
}

const hasRoleHelpText = (roleId: string): boolean => {
    return roleId === RoleID.ReadOnlySystemLogs || roleId === RoleID.ReadOnlySuperAdmin
}

const RoleHelpText = ({ role }: { role: Role }): React.ReactElement | null => {
    switch (role.id) {
        case RoleID.ReadOnlySystemLogs:
            return <Typography variant="caption" component="span" color="text.secondary">
                <FormattedMessage
                    id="sp_credential_role_read_only_system_logs"
                    defaultMessage="Can view system logs only."
                />
            </Typography>
        case RoleID.ReadOnlySuperAdmin:
            return <Typography variant="caption" component="span" color="text.secondary">
                <FormattedMessage
                    id="sp_credential_role_read_only_super_admin"
                    defaultMessage="Read-only access to all features."
                />
            </Typography>
        default:
            return null
    }
}

export const CreateSPCredentialDrawer: FC<CreateSPCredentialDrawerProps> = ({ close, servicePrincipalId }) => {
    const intl = useIntl()
    const { mutate: createCredential, inProgress, error, data: result } = createServicePrincipalCredentialApi()
    const { data: allRoles } = useGetAllRoles(SearchRolesRequest.create({}))

    const schema = useMemo(() => yup.object().shape({
        displayName: yup.string()
            .required(intl.formatMessage({
                id: 'sp_credential_display_name_required',
                defaultMessage: 'Display name is required',
            }))
            .trim()
            .min(1, intl.formatMessage({
                id: 'sp_credential_display_name_empty',
                defaultMessage: 'Display name cannot be empty',
            }))
            .max(512, intl.formatMessage({
                id: 'sp_credential_display_name_max',
                defaultMessage: 'Display name must be less than 512 characters',
            })),
        expiresDays: yup.number().required(),
        allowSourceCidrs: yup.array().of(yup.string().required()).default([]),
        scopedRole: yup.string().required().default('*'),
        requireDpop: yup.boolean().default(false),
    }), [intl])

    const { register, control, handleSubmit, formState: { errors } } = useForm<FormData>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            displayName: '',
            expiresDays: 90,
            allowSourceCidrs: [],
            scopedRole: '*',
            requireDpop: false,
        },
    })

    const [roleOptions, setRoleOptions] = useState<{ id: string; value: string; label: ReactNode; sx?: object }[]>([])

    useEffect(() => {
        if (!allRoles || allRoles.length === 0) {
            return
        }
        const existingRoleOptions = allRoles.flat().filter(
            (role) => role.systemApiOnly === true || role.id === RoleID.BasicUser || role.id === RoleID.ReadOnlySuperAdmin,
        ).map((role: Role) => ({
            id: role.id,
            value: role.id,
            label: hasRoleHelpText(role.id)
                ? <Stack direction="column">
                    <Typography component="span">{role.displayName}</Typography>
                    <RoleHelpText role={role} />
                </Stack>
                : <Typography component="span">{role.displayName}</Typography>,
            sx: hasRoleHelpText(role.id) ? { alignItems: 'flex-start', mb: 1 } : { mb: 1 },
        }))

        existingRoleOptions.push({
            id: '*',
            value: '*',
            label: <Stack direction="column">
                <Typography component="span">
                    <FormattedMessage
                        id="sp_credential_scope_full_permissions"
                        defaultMessage="Full permissions"
                    />
                </Typography>
                <Typography variant="caption" component="span" color="text.secondary">
                    <FormattedMessage
                        id="sp_credential_scope_full_permissions_caption"
                        defaultMessage="All permissions assigned to this service principal."
                    />
                </Typography>
            </Stack>,
            sx: { alignItems: 'flex-start', mb: 1 },
        })
        existingRoleOptions.sort((a, b) => a.id.localeCompare(b.id))

        setRoleOptions(existingRoleOptions)
    }, [allRoles])

    const expirationOptions = useMemo(() => [
        {
            id: '30',
            value: 30,
            label: intl.formatMessage({
                id: 'sp_credential_expiration_30_days',
                defaultMessage: '30 days',
            }),
        },
        {
            id: '60',
            value: 60,
            label: intl.formatMessage({
                id: 'sp_credential_expiration_60_days',
                defaultMessage: '60 days',
            }),
        },
        {
            id: '90',
            value: 90,
            label: intl.formatMessage({
                id: 'sp_credential_expiration_90_days',
                defaultMessage: '90 days (recommended)',
            }),
        },
        {
            id: '180',
            value: 180,
            label: intl.formatMessage({
                id: 'sp_credential_expiration_180_days',
                defaultMessage: '180 days (maximum)',
            }),
        },
    ], [intl])

    const onSubmit = useCallback(async (data: FormData): Promise<void> => {
        if (inProgress) return
        await createCredential(ServicePrincipalServiceCreateCredentialRequest.create({
            servicePrincipalId,
            displayName: data.displayName.trim(),
            expires: Duration.create({ seconds: data.expiresDays * 86400 }),
            allowSourceCidrs: data.allowSourceCidrs,
            scopedRoles: data.scopedRole !== '*' ? [data.scopedRole] : [],
            requireDpop: data.requireDpop,
        }))
    }, [createCredential, servicePrincipalId, inProgress])

    if (result) {
        return <C1DrawerInner>
            <C1DrawerInner.Header>
                <FormattedMessage id="sp_credential_created_title" defaultMessage="Credential created" />
            </C1DrawerInner.Header>
            <C1DrawerInner.Content>
                <Stack spacing={3}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <SuccessCheck />
                        <Typography variant="h6">
                            <FormattedMessage id="sp_credential_created_success" defaultMessage="Credential created successfully" />
                        </Typography>
                    </Stack>
                    <Alert severity="warning">
                        <FormattedMessage
                            id="sp_credential_secret_warning"
                            defaultMessage="Store these values securely. The client secret will not be shown again."
                        />
                    </Alert>
                    <Box>
                        <Typography variant="h6" pb={1}>
                            <FormattedMessage id="sp_credential_client_id_label" defaultMessage="Client ID" />
                        </Typography>
                        <C1CopyText value={result.credential?.clientId} typographyVariant="monospace" />
                    </Box>
                    <Box>
                        <Typography variant="h6" pb={1}>
                            <FormattedMessage id="sp_credential_client_secret_label" defaultMessage="Client secret" />
                        </Typography>
                        <C1CopyText value={result.clientSecret} typographyVariant="monospace" />
                    </Box>
                </Stack>
            </C1DrawerInner.Content>
            <C1DrawerInner.Footer>
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <C1Button onClick={close} variant="contained">
                        <FormattedMessage id="sp_credential_done" defaultMessage="Done" />
                    </C1Button>
                </Stack>
            </C1DrawerInner.Footer>
        </C1DrawerInner>
    }

    return <C1DrawerInner>
        <C1DrawerInner.Header>
            <FormattedMessage id="sp_credential_create_title" defaultMessage="Create credential" />
        </C1DrawerInner.Header>
        <C1DrawerInner.Content>
            <APIError err={error} />
            <Stack spacing={3}>
                <TextField
                    label={<FormattedMessage id="sp_credential_display_name_label" defaultMessage="Display name" />}
                    {...register('displayName')}
                    error={!!errors.displayName}
                    helperText={errors.displayName?.message}
                    fullWidth
                />
                <C1LabeledSelect
                    control={control}
                    name="expiresDays"
                    label={
                        <FormattedMessage
                            id="sp_credential_expiration_label"
                            defaultMessage="Expiration"
                        />
                    }
                    options={expirationOptions}
                    error={!!errors.expiresDays}
                    helperText={errors.expiresDays?.message}
                    fullWidth
                />
                <Box>
                    <C1FormLabel
                        label={
                            <FormattedMessage
                                id="sp_credential_cidr_label"
                                defaultMessage="Limit source IPs"
                            />
                        }
                        htmlFor="allowSourceCidrs"
                    />
                    <Controller
                        name="allowSourceCidrs"
                        control={control}
                        render={({ field }): React.ReactElement =>
                            <CIDRChipInput
                                aria-label={intl.formatMessage({
                                    id: 'sp_credential_cidr_aria',
                                    defaultMessage: 'Limit source IPs',
                                })}
                                editable
                                value={field.value}
                                setValue={(values): void => field.onChange(values)}
                            />
                        }
                    />
                </Box>
                <C1LabeledRadioGroup
                    control={control}
                    name="scopedRole"
                    label={intl.formatMessage({
                        id: 'sp_credential_scope_label',
                        defaultMessage: 'Limit scopes',
                    })}
                    options={roleOptions}
                    error={!!errors.scopedRole}
                    helperText={errors.scopedRole?.message}
                />
                <C1LabeledSwitch
                    control={control}
                    name="requireDpop"
                    label={
                        <FormattedMessage
                            id="sp_credential_dpop_label"
                            defaultMessage="Require DPoP"
                        />
                    }
                    error={!!errors.requireDpop}
                    helperText={intl.formatMessage({
                        id: 'sp_credential_dpop_helper',
                        defaultMessage: 'Requires proof-of-possession (DPoP) for token requests. Enable if your client supports RFC 9449.',
                    })}
                />
            </Stack>
        </C1DrawerInner.Content>
        <C1DrawerInner.Footer>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
                <C1Button onClick={close} variant="outlined">
                    <FormattedMessage id="sp_credential_cancel" defaultMessage="Cancel" />
                </C1Button>
                <C1Button onClick={handleSubmit(onSubmit)} disabled={inProgress} variant="contained">
                    <FormattedMessage id="sp_credential_create" defaultMessage="Create" />
                </C1Button>
            </Stack>
        </C1DrawerInner.Footer>
    </C1DrawerInner>
}
